import logger from '../utils/logger';
import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import '../style/CheckIn.css';
import { formatTo12Hour, getUserId, formatAppointmentTimeRange, convertLocalToUTC, dateOfBirth } from '../utils/utils';
import { GlobalStateContext } from "../utils/GlobalStateContext";
import PageTopBar from './customComponents/PageTopBar';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Home, Pencil, Save, MapPin } from 'lucide-react';
import LocationModal from './customComponents/LocationModal';
import { getLocationStageColor } from '../utils/utils';

const locations = [
  'Scheduled',
  'Registration',
  'Waiting room',
  'Lab',
  'Radiology',
  'Clinic 1',
  'Clinic 2',
  'Clinic 3',
  'Complete'
];

function CheckInPage() {
  const [appointments, setAppointments] = useState([]);
  const [editingLocation, setEditingLocation] = useState(null);
  const [registrationModal, setRegistrationModal] = useState(null);
  const [locationModalAppt, setLocationModalAppt] = useState(null);
  const navigate = useNavigate();
  const { currentPatient, setCurrentPatient } = useContext(GlobalStateContext);
  const { currentUser } = useContext(GlobalStateContext);
  const BASE_URL = process.env.REACT_APP_API_URL;
  const user = getUserId(currentUser);

  useEffect(() => {
    const fetchAppointments = async () => {
      try {
        const response = await fetch(`${BASE_URL}/patients/patientCheckIn?user=${user}&type=${currentUser.type}`);
        if (response.status === 200) {
          const data = await response.json();
          setAppointments(data);
        }
      } catch (error) {
        logger.error("Error fetching appointments", error);
      }
    };

    fetchAppointments();
  }, []);

  const handleCheckIn = async (id) => {
    const currentTime = convertLocalToUTC(new Date());

    try {
      const response = await fetch(`${BASE_URL}/patients/patientCheckIn`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, currentTime, user, type: currentUser.type }),
      });

      if (response.ok) {
        setAppointments((prevAppointments) =>
          prevAppointments.map((appt) => {
            if (appt.id === id) {
              return { ...appt, check_in_time: currentTime, location: 'Registration' };
            }
            return appt;
          })
        );
        const fetchResponse = await fetch(`${BASE_URL}/patients/patientCheckIn?user=${user}&type=${currentUser.type}`);
        if (fetchResponse.status === 200) {
          const data = await fetchResponse.json();
          setAppointments(data);
        }
      } else {
        logger.error('Failed to update check-in time:', await response.json());
      }
    } catch (error) {
      logger.error('Error during check-in:', error);
    }
  };

  // Navigate to the per-appointment payment workflow (HGIPayments), which builds
  // the bill for the selected appointment before handing off to the card screen.
  const goToPayment = (appt) => {
    setCurrentPatient({ ris_patients_id: appt.ris_patients_id });
    navigate('/hgiPayments', { state: { appointmentId: appt.id } });
  };

  const handleLocationChange = async (appointment, newLocation) => {
    try {
      const response = await fetch(`${BASE_URL}/patients/editPatientLocation`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: appointment.id, location: newLocation, user: user, type: currentUser.type }),
      });

      if (response.ok) {
        setAppointments((prevAppointments) =>
          prevAppointments.map((appt) =>
            appt.first_name === appointment.first_name &&
            appt.last_name === appointment.last_name &&
            appt.appointment_start === appointment.appointment_start
              ? { ...appt, location: newLocation }
              : appt
          )
        );
        setEditingLocation(newLocation);

        // Moving the patient to 'Complete' checks them out — mirror Scheduling's
        // checkout flow by offering a one-tap jump to collect payment.
        if (newLocation === 'Complete') {
          toast.success(
            ({ closeToast }) => (
              <div className="checkout-toast">
                <span>{appointment.first_name} {appointment.last_name} checked out.</span>
                <button
                  className="checkout-toast-btn"
                  onClick={() => { closeToast(); goToPayment(appointment); }}
                >
                  Collect payment
                </button>
              </div>
            ),
            { autoClose: 8000 }
          );
        }
      } else {
        logger.error('Failed to update location:', await response.json());
      }
    } catch (error) {
      logger.error('Error updating location:', error);
    }
  };

  // Location modal's stepper -- moves the appointment to a new stage and
  // records it in appointment_status_log (via /scheduling/updateStatus) so
  // time-per-stage can be shown the next time the modal opens.
  const handleStatusChange = async (appointment, newLocation) => {
    try {
      const response = await fetch(`${BASE_URL}/scheduling/updateStatus`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ schedule_id: appointment.schedule_id, status: newLocation, user, type: currentUser.type }),
      });
      if (response.ok) {
        setAppointments((prev) =>
          prev.map((appt) => (appt.id === appointment.schedule_id ? { ...appt, location: newLocation } : appt))
        );
        setLocationModalAppt(null);
      } else {
        logger.error('Failed to update status:', await response.json());
      }
    } catch (error) {
      logger.error('Error updating status:', error);
    }
  };

  // Reopens an abandoned registration with its saved answers restored. Taken
  // from the Location modal against a named patient, never offered by the
  // registration form itself -- see hooks/useRegistrationDraft.js.
  const handleResumeRegistration = (appointment) => {
    if (!appointment) return;
    setLocationModalAppt(null);
    navigate(`/patientRegistration?ris_patients_id=${appointment.ris_patients_id}`, {
      state: { restoreDraft: true },
    });
  };

  const handleEditClick = () => {
    setEditingLocation(editingLocation ? null : true);
  };

  const sortedAppointments = [...appointments].sort((a, b) => {
    if (a.patientName < b.patientName) return -1;
    if (a.patientName > b.patientName) return 1;
    const order = { Clinic: 1, Lab: 2, Radiology: 3 };
    return (order[a.typeOfVisit] || 4) - (order[b.typeOfVisit] || 4);
  });

  return (
    <div className="checkin-page">
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
      <PageTopBar links={[
        { label: 'Home', to: '/home', icon: Home },
      ]} />

      <div className="checkin-content">
        <div className="checkin-page-header">
          <div>
            <h1 className="checkin-page-title">Patient Check-In</h1>
            <p className="checkin-page-subtitle">Today's Date: {new Date().toLocaleDateString()}</p>
          </div>
          <button className="checkin-edit-btn" onClick={handleEditClick}>
            {editingLocation ? <><Save size={15} /><span>Save</span></> : <><Pencil size={15} /><span>Edit Locations</span></>}
          </button>
        </div>

        <div className="checkin-table-wrapper">
          <table className="checkin-table">
            <thead>
              <tr className="checkin-thead-row">
                <th className="checkin-th">Patient Name</th>
                <th className="checkin-th">Type of Visit</th>
                <th className="checkin-th">Exam</th>
                <th className="checkin-th">Check-In Time</th>
                <th className="checkin-th">Time Scheduled</th>
                <th className="checkin-th">Location</th>
              </tr>
            </thead>
            <tbody>
              {sortedAppointments.map((appt) => (
                <tr key={appt.id} className="checkin-tbody-row">
                  <td className="checkin-td checkin-td-name">
                    {appt.first_name} {appt.last_name}
                    <button
                      className="checkin-view-btn"
                      onClick={() => {
                        setCurrentPatient({
                          ris_patients_id: appt.ris_patients_id,
                          new_patients_id: appt.new_patients_id,
                          first_name: appt.first_name,
                          last_name: appt.last_name,
                          appointment_start: appt.appointment_start,
                          appointment_end: appt.appointment_end,
                          visit_type: appt.visit_type,
                          location: appt.location,
                          check_in_time: appt.check_in_time,
                        });
                        navigate('/patientAbout');
                      }}
                    >
                      View
                    </button>
                  </td>
                  <td className="checkin-td">{appt.visit_type}</td>
                  <td className="checkin-td">{appt.exam || '—'}</td>
                  <td className="checkin-td">
                    {appt.check_in_time ? formatTo12Hour(appt.check_in_time) : (
                      <button onClick={() => handleCheckIn(appt.id)} className="checkin-checkin-btn">
                        Check In
                      </button>
                    )}
                  </td>
                  <td className="checkin-td">{formatAppointmentTimeRange(appt.appointment_start, appt.appointment_end)}</td>
                  <td className="checkin-td">
                    {editingLocation ? (
                      <select
                        value={appt.location}
                        onChange={(e) => handleLocationChange(appt, e.target.value)}
                        className="checkin-location-select"
                      >
                        {locations.map((location) => (
                          <option key={location} value={location}>{location}</option>
                        ))}
                      </select>
                    ) : (
                      <div className="checkin-location-cell">
                        {appt.location === "Registration"
                          ? <button className="checkin-registration-link" onClick={() => setRegistrationModal(appt)}>{appt.location}</button>
                          : <span className={`checkin-location-badge checkin-location-${appt.location?.toLowerCase().replace(/\s+/g, '-')}`}>{appt.location}</span>
                        }
                        <button
                          className="checkin-location-modal-btn"
                          style={{ color: getLocationStageColor(appt.location) }}
                          title="View location history / time in each stage"
                          onClick={() => setLocationModalAppt(appt)}
                        >
                          <MapPin size={15} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {registrationModal && (
        <div className="checkin-modal-overlay" onClick={() => setRegistrationModal(null)}>
          <div className="checkin-modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="checkin-modal-title">Confirm Patient Identity</h2>
            <p className="checkin-modal-subtitle">Please verify the following information with the patient before proceeding.</p>
            <div className="checkin-modal-fields">
              <div className="checkin-modal-field">
                <span className="checkin-modal-label">First Name</span>
                <span className="checkin-modal-value">{registrationModal.first_name}</span>
              </div>
              <div className="checkin-modal-field">
                <span className="checkin-modal-label">Last Name</span>
                <span className="checkin-modal-value">{registrationModal.last_name}</span>
              </div>
              <div className="checkin-modal-field">
                <span className="checkin-modal-label">Date of Birth</span>
                <span className="checkin-modal-value">
                  {registrationModal.dob ? dateOfBirth(registrationModal.dob) : '—'}
                </span>
              </div>
            </div>
            <div className="checkin-modal-actions">
              <button className="checkin-modal-cancel" onClick={() => setRegistrationModal(null)}>Cancel</button>
              <button
                className="checkin-modal-confirm"
                onClick={() => {
                  window.location.href = `/patientRegistration?ris_patients_id=${registrationModal.ris_patients_id}`;
                }}
              >
                Confirmed — Open Registration
              </button>
            </div>
          </div>
        </div>
      )}

      {locationModalAppt && (
        <LocationModal
          appointment={{
            schedule_id: locationModalAppt.id,
            ris_patients_id: locationModalAppt.ris_patients_id,
            patient_name: `${locationModalAppt.first_name} ${locationModalAppt.last_name}`,
          }}
          currentStatus={locationModalAppt.location}
          onSelectStatus={(newLocation) => handleStatusChange(locationModalAppt, newLocation)}
          onResumeRegistration={handleResumeRegistration}
          onCancel={() => setLocationModalAppt(null)}
        />
      )}
    </div>
  );
}

export default CheckInPage;
